import React, {forwardRef, useContext, useMemo} from 'react';

import {FloatingMenuContext, FloatingPopover} from '@pb/kit-component-floating';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './menu.module.styl';

import type {FloatingMenuContextType, FloatingPopoverProps} from '@pb/kit-component-floating';


const classNames = withCssModule(styles);

export interface FloatingMenuProps extends FloatingPopoverProps {
    /** Callback from `useFloating` that merges keyboard-navigation props onto
     *  each `FloatingMenuItem` — passed via `FloatingMenuContext`. */
    getItemProps: FloatingMenuContextType['getItemProps'],
    /** Index of the currently keyboard-focused item (`null` when none). Passed
     *  via `FloatingMenuContext` so each item can derive its highlighted state. */
    activeIndex: FloatingMenuContextType['activeIndex'],
    /** Setter for `activeIndex`, forwarded to `FloatingMenuContext` for
     *  sub-menu navigation or programmatic focus control. */
    setActiveIndex?: (activeIndex: (number | null)) => void,
}

/**
 * Floating overlay panel that hosts a keyboard-navigable menu.
 *
 * Intended as an internal building block for higher-level components (e.g.
 * `DropdownMenu`, `ContextMenu`). Wraps `FloatingPopover` with a
 * `FloatingMenuContext` that propagates `getItemProps`, `activeIndex`, and
 * nesting `level` to all descendant `FloatingMenuItem` nodes.
 *
 * Obtain `getItemProps` and `activeIndex` from `useFloating()` or equivalent,
 * then pass them alongside any `FloatingPopover` props (`getContext`,
 * `isShown`, etc.).
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-menu--documentation}
 */
export const FloatingMenu = forwardRef<HTMLDivElement, FloatingMenuProps>((props, ref) => {
    const floatingMenuContextParentValue = useContext(FloatingMenuContext);

    const {
        children,
        className,
        getItemProps,
        activeIndex,
        portalProps = floatingMenuContextParentValue.portalProps,
        floatingFocusManagerProps = floatingMenuContextParentValue.floatingFocusManagerProps,
        ...floatingPopoverProps
    } = props;
    const floatingClasses = classNames(className, 'floating');


    const level = floatingMenuContextParentValue.level + 1;

    const floatingMenuContextValue = useMemo(() => ({
        getItemProps,
        activeIndex,
        level,
        portalProps,
        floatingFocusManagerProps,
    }), [getItemProps, activeIndex, level, portalProps, floatingFocusManagerProps]);

    return (
        <FloatingMenuContext.Provider value={floatingMenuContextValue}>
            <FloatingPopover
                ref={ref}
                floatingFocusManagerProps={floatingFocusManagerProps}
                portalProps={portalProps}
                {...floatingPopoverProps}
                className={floatingClasses}>
                {children}
            </FloatingPopover>
        </FloatingMenuContext.Provider>
    );
});

FloatingMenu.displayName = 'FloatingMenu';
